import { PATHS } from '../../content/paths';
import type { UpgradeDef } from '../../content/upgrades';
import { fmtDate, fmtEur } from '../../engine/format';
import { t } from '../../engine/i18n';
import type { GameState } from '../../engine/state';
import { update } from '../../engine/store';
import { DAYS_PER_YEAR, dateAtDay } from '../../engine/time';
import {
  SKILLS,
  TRAIN_XP,
  careerActive,
  levelsSinceHire,
  levelsSinceRaise,
  nextPromotion,
  setFocus,
  startingSalary,
  train,
  xpMultiplier,
  xpToNext,
} from '../../game/career';
import { buyUpgrade, daysBeforeWorn, upgradePrice, visibleUpgrades } from '../../game/upgrades';
import { currentMood, isLowEnergy } from '../../game/wellbeing';
import { basePay, energyBlocker, energyMax, payPerClick, work, workMode } from '../../game/work';
import { Card, Meter } from '../components';
import { CATEGORY_LABEL, formatDuration } from '../labels';

export function WorkTab({ state }: { state: GameState }) {
  const mode = workMode(state);
  const blocker = energyBlocker(state);
  const pay = fmtEur(payPerClick(state));
  const max = energyMax(state);
  const workLabel =
    mode === 'missions' ? t('work.missionButton', { amount: pay }) : t('work.smallJobButton', { amount: pay });
  const mood = currentMood(state);
  const restDate = fmtDate(dateAtDay(state.startDate, state.restUntilDay));
  const fatigueDate = fmtDate(dateAtDay(state.startDate, state.fatigueUntilDay));

  return (
    <div class="grid">
      <Card title={t('work.title')}>
        <button class="button-primary button-work" disabled={blocker !== null} onClick={() => update(work)}>
          {workLabel}
        </button>
        {blocker === 'work.resting' && <p class="warning">{t('work.resting', { date: restDate })}</p>}
        {blocker === 'work.noEnergy' && <p class="warning">{t('work.noEnergy')}</p>}
        <p class="meter-label">
          {t('dash.energy')}
          <span>{t('dash.energyValue', { current: Math.floor(state.energy), max })}</span>
        </p>
        <Meter value={state.energy} max={max} tone="energy" label={t('dash.energy')} />
        {isLowEnergy(state) && <p class="warning">{t('work.lowEnergy')}</p>}
        <p class="muted">{t('work.energyCost')}</p>
        <p class="hint">{t('work.lowEnergyRule')}</p>
        {state.day < state.fatigueUntilDay && <p class="warning">{t('work.fatigue', { date: fatigueDate })}</p>}
        {mood.id !== 'normal' && (
          <p class={mood.id === 'forme' ? 'tone-good' : 'warning'}>
            {t('dash.mood', { label: t(mood.label), effect: t(mood.effect) })}
          </p>
        )}
        {state.earnedByClick > 0 && (
          <p class="muted">{t('work.earnedByClick', { amount: fmtEur(state.earnedByClick) })}</p>
        )}
      </Card>

      <Card title={t('career.title')}>
        {careerActive(state) ? (
          <Career state={state} blocked={blocker !== null} />
        ) : (
          <p class="muted">{t('career.locked')}</p>
        )}
      </Card>

      {state.energyUnlocked && <Shop state={state} />}
    </div>
  );
}

function Career({ state, blocked }: { state: GameState; blocked: boolean }) {
  const next = nextPromotion(state);
  const employed = state.stage !== 'prologue';
  return (
    <>
      {employed ? (
        <p>{t('career.salary', { salary: fmtEur(state.salary) })}</p>
      ) : (
        <>
          <p>
            {t('career.startingSalary', {
              salary: fmtEur(startingSalary(state, PATHS[state.path ?? 'work'].salary)),
            })}
          </p>
          <p class="muted">{t('career.prologueHint')}</p>
        </>
      )}
      <p class="muted">{t('career.intro')}</p>
      <ul class="skills">
        {SKILLS.map((skill) => {
          const current = state.skills[skill.id];
          const focused = state.focusSkill === skill.id;
          return (
            <li key={skill.id} class={focused ? 'focused' : ''}>
              <div class="skill-head">
                <strong>{t(skill.label)}</strong>
                <span>{t('career.level', { level: current.level })}</span>
              </div>
              <Meter value={current.xp} max={xpToNext(current.level)} label={t(skill.label)} />
              <div class="skill-foot">
                <span class="muted">
                  {t('career.xp', { xp: Math.floor(current.xp), next: xpToNext(current.level) })}
                </span>
                {focused ? (
                  <span class="tag">{t('career.focused')}</span>
                ) : (
                  <button class="button-small" onClick={() => update((s) => setFocus(s, skill.id))}>
                    {t('career.focus')}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <button disabled={blocked} onClick={() => update(train)}>
        {t('career.train', { xp: Math.round(TRAIN_XP * xpMultiplier(state)) })}
      </button>
      {employed && (
        <>
          <p class="muted">{t('career.promotions', { count: state.promotions })}</p>
          <p class="muted">
            {next
              ? t(state.promotions === 0 ? 'career.nextPromotionFirst' : 'career.nextPromotion', {
                  levels: next.levels,
                  current: levelsSinceHire(state),
                  years: formatDuration(next.years * DAYS_PER_YEAR),
                })
              : t('career.maxPromotions')}
          </p>
          <p class="muted">
            {t('career.raiseHint', { count: levelsSinceRaise(state) })}
          </p>
        </>
      )}
    </>
  );
}

/** Texte « rentabilisé en … » d'un achat, s'il a un effet sur l'argent gagné. */
function paybackText(state: GameState, upgrade: UpgradeDef): string | null {
  const { effect } = upgrade;
  if (effect.salary) {
    const salary = state.firstJobDay !== null ? state.salary : PATHS[state.path ?? 'work'].salary;
    const gain = salary * effect.salary;
    return gain > 0 ? t('shop.paybackMonths', { months: Math.ceil(upgradePrice(state, upgrade) / gain) }) : null;
  }
  // Un achat qui fait aussi gagner de l'expérience ne se résume pas à son gain par heure.
  if (effect.pay && !effect.xp) {
    const gain = basePay(state) * effect.pay;
    return gain > 0 ? t('shop.paybackHours', { hours: Math.ceil(upgradePrice(state, upgrade) / gain) }) : null;
  }
  return null;
}

/** La vétusté du matériel : la jauge se remplit jusqu'au jour où il est usé. */
function WearMeter({ state, upgrade }: { state: GameState; upgrade: UpgradeDef }) {
  const left = daysBeforeWorn(state, upgrade) ?? 0;
  const lifespan = (upgrade.lifespan?.years ?? 0) * DAYS_PER_YEAR;
  return (
    <>
      <Meter value={lifespan - left} max={lifespan} tone="wear" label={t('shop.wear')} />
      <p class="hint">{t('shop.wearsIn', { duration: formatDuration(left) })}</p>
    </>
  );
}

function Shop({ state }: { state: GameState }) {
  return (
    <Card title={t('shop.title')} className="card-wide">
      <p class="muted">{t('shop.intro')}</p>
      <ul class="shop">
        {visibleUpgrades(state).map((upgrade) => {
          const owned = state.upgrades.includes(upgrade.id);
          const worn = state.wornUpgrades.includes(upgrade.id);
          const payback = owned ? null : paybackText(state, upgrade);
          const cost = upgradePrice(state, upgrade);
          const price = fmtEur(cost);
          return (
            <li key={upgrade.id} class={owned ? 'owned' : worn ? 'worn' : ''}>
              <div class="shop-head">
                <strong>{t(upgrade.title)}</strong>
                <span class="tag">{t(CATEGORY_LABEL[upgrade.category])}</span>
              </div>
              <p class="muted">{t(upgrade.desc)}</p>
              {upgrade.lifespan && !owned && !worn && (
                <p class="hint">
                  {t('shop.lifespan', { duration: formatDuration(upgrade.lifespan.years * DAYS_PER_YEAR) })}
                </p>
              )}
              {worn && <p class="warning">{t('shop.wornHint')}</p>}
              {payback && <p class="hint">{payback}</p>}
              {owned && upgrade.lifespan && <WearMeter state={state} upgrade={upgrade} />}
              {owned ? (
                <span class="tag">{t('shop.owned')}</span>
              ) : (
                <button disabled={state.cash < cost} onClick={() => update((s) => buyUpgrade(s, upgrade.id))}>
                  {worn ? t('shop.replace', { price }) : t('shop.buy', { price })}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
